import axios, { AxiosInstance, AxiosResponse, AxiosPromise } from 'axios'
import preferenceService from './preferences'
import { DateTime } from 'luxon'
import { SignUpParameters } from '@/store/auth/definitions'
import { DRFMessageData } from '@/store/message/definitions'
import { WeatherData, WeatherStationDataFromAPI } from '@/store/weatherstation/definitions'
import { AverageTemperatureDataFromAPI } from '@/store/avgtemp/definitions'
import { AirParifDataFromAPI } from '@/store/airparif/definitions'

export const SIGNATURE_HAS_EXPIRED = 'Signature has expired.'
export const ERROR_DECODING_SIGNATURE = 'Error decoding signature.'

function addTokenToHeader (config) {
  const token = preferenceService.token
  if (token) config.headers.Authorization = 'JWT ' + token
  return config
}

axios.defaults.headers.common['X-Requested-With'] = MFS_APP_VERSION

class ApiService {
  /**
   * Useful to know if a refresh token is in progress.
   *
   * If it is, we must refresh only one time.
   *
   * If the result is an error,
   * we must return the error to the caller.
   */
  private _refreshingToken = false
  private _instance: AxiosInstance
  private _interceptorTokenID: number

  constructor () {
    this._instance = axios.create({
      baseURL: SITE_API_URL
    })

    this._interceptorTokenID = this._instance.interceptors.request.use(addTokenToHeader)
  }

  setErrorHandler (errorHandler) {
    if (errorHandler) {
      this._instance.interceptors.response.use(
        function (response) {
          return response
        },
        function (error) {
          errorHandler(error)
          return Promise.reject(error)
        }
      )
    }
  }

  /**
   * Token endpoints
   */
  refreshToken (token) {
    return this._instance.request({
      method: 'post',
      url: 'refresh/',
      data: {
        token
      }
    })
  }

  /**
   * User endpoints
   */
  login (username: string, password: string): Promise<AxiosResponse> {
    return this._instance.request({
      method: 'post',
      url: 'login/',
      data: {
        username,
        password
      }
    })
  }

  logout () {
    return this._instance
      .request({
        method: 'post',
        url: 'logout/'
      })
  }

  /**
   * Config endpoint
   */
  getConfig () {
    this._instance.interceptors.request.eject(this._interceptorTokenID)
    const request = this._instance.request({
      method: 'get',
      url: 'config/'
    })
    this._interceptorTokenID = this._instance.interceptors.request.use(addTokenToHeader)
    return request
  }

  /**
   * Events endpoints
   */
  getActiveEvent () {
    return this._instance.request({
      method: 'get',
      url: 'events/active/'
    })
  }

  getEvent (eventId: number | string) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/'
    })
  }

  getEvents () {
    return this._instance.request({
      method: 'get',
      url: 'events/'
    })
  }

  getEventsByCode (code: string) {
    return this._instance.request({
      method: 'get',
      url: `events/?code=${code}`
    })
  }

  /**
   * Forecast endpoints
   */
  getForecastDaily (eventId) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/forecast/daily/'
    })
  }

  getForecastHourly (eventId) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/forecast/hourly/'
    })
  }

  getForecastRain (eventId) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/forecast/rain/'
    })
  }

  /**
   * Weather station endpoints,
   *
   * All functions worked with station ids,
   * when results are emitted for several stations,
   * an object is returned instead of an array of indexed stations
   */
  async getStationDataLatest (id: number): Promise<WeatherStationDataFromAPI> {
    const response = await this._instance.request({
      method: 'get',
      url: 'weatherstation/' + id + '/latest/'
    })
    if (response.data.car_wind_direction) {
      response.data.data.WeatherData.CarWindDirection = response.data.car_wind_direction
    }
    return response.data as WeatherStationDataFromAPI
  }

  async getStationsDataLatest (stationsId: string[]): Promise<Record<string, WeatherStationDataFromAPI>> {
    const result = {}
    stationsId.forEach(currentStationId => (result[currentStationId] = []))
    return stationsId.reduce(async (previousValue, currentStationId: string) => {
      await previousValue
      const currentStationDataLatest = await this.getStationDataLatest(parseInt(currentStationId))
      previousValue[currentStationId] = currentStationDataLatest.data
      return previousValue
    }, Promise.resolve({}) as Promise<Record<string, WeatherStationDataFromAPI>>)
  }

  /**
   * Retrieve station data in a date interval,
   * sort result by value_date,
   * add the wind direction in the data returned
   *
   * @param stationId Id of the station in the database (not its reference)
   * @param dateFrom from which date
   * @param dateTo to which date
   * @returns an object whose keys are station ids and values are { data, station }
   */
  async getStationDataFilteredByDate (stationId: string, dateFrom: DateTime, dateTo: DateTime): Promise<{ data: WeatherData; station: number}[]> {
    const response = await this._instance.request({
      method: 'get',
      url: 'weatherstation/' + stationId + '/datas/',
      params: {
        date_from: dateFrom.toFormat('yyyy-MM-dd\'T\'HH:mm:ss'),
        date_to: dateTo.toFormat('yyyy-MM-dd\'T\'HH:mm:ss')
      }
    }) as AxiosResponse<WeatherStationDataFromAPI[]>

    const result = response.data
      .sort((a, b) => (b.value_date.localeCompare(a.value_date)))
      .map(r => {
        r.data.WeatherData.CarWindDirection = r.car_wind_direction
        return {
          data: r.data.WeatherData,
          station: r.station
        }
      }) as { data: WeatherData; station: number}[]

    return result
  }

  async getStationsDataFilteredByDate (stationsId: string[], dateFrom: DateTime, dateTo: DateTime): Promise<Record<string, { data: WeatherData; station: number}[]>> {
    const result = {}
    stationsId.forEach(currentStationId => (result[currentStationId] = []))
    await stationsId.reduce(async (previousValue, currentStationId: string) => {
      await previousValue
      const currentStationData = await this.getStationDataFilteredByDate(currentStationId, dateFrom, dateTo)
      result[currentStationId] = currentStationData
    }, Promise.resolve())
    return result
  }

  /**
   * Average temperature
   */
  getAverageTemperatureLatest (eventId: number): Promise<AxiosResponse<AverageTemperatureDataFromAPI>> {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/avgtemp/latest/'
    })
  }

  getAverageTemperatureFilteredByDate (eventId: number, dateFrom: DateTime, dateTo: DateTime): Promise<AxiosResponse<AverageTemperatureDataFromAPI[]>> {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/avgtemp/',
      params: {
        date_from: dateFrom.toFormat('yyyy-MM-dd\'T\'HH:mm:ss'),
        date_to: dateTo.toFormat('yyyy-MM-dd\'T\'HH:mm:ss')
      }
    })
  }

  /**
   * Radar endpoints
   */
  getRadarAvailableTimesLatest (eventId: number) {
    return this._instance
      .request({ method: 'get', url: 'events/' + eventId + '/radar/latest/' })
      .catch(() => {
        return { data: null }
      })
  }

  getRadarBbox (eventId: number) {
    return this._instance
      .request({ method: 'get', url: 'events/' + eventId + '/radar/bbox/' })
      .catch(() => {
        return { data: null }
      })
  }

  getAllWeatherStationDataForEventSessions (eventId: number) {
    return this._instance
      .request({
        method: 'get',
        url: 'events/' + eventId + '/stationdata/allsessions/'
      })
  }

  /**
   * Messages endpoint
   */
  getMessages (eventId): AxiosPromise<DRFMessageData[]> {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/messages/'
    })
  }

  /**
   * Infos endpoint
   */
  getInfos (eventId) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/infos/'
    })
  }

  getStatistics (dateFrom: DateTime, dateTo: DateTime) {
    return this._instance.request({
      method: 'get',
      url: 'statistics/',
      params: {
        date_from: dateFrom.toFormat('yyyy-MM-dd\'T\'HH:mm:ss'),
        date_to: dateTo.toFormat('yyyy-MM-dd\'T\'HH:mm:ss'),
        limit: 100000
      }
    })
  }

  /**
   * Airparif endpoint
   */
  async getAirParifData (eventId): Promise<AxiosResponse<AirParifDataFromAPI[]>> {
    return await this._instance.request<AirParifDataFromAPI[]>({
      method: 'get',
      url: 'events/' + eventId + '/airparif/'
    })
  }

  getUser () {
    return this._instance.request({
      method: 'get',
      url: 'me/'
    })
  }

  updateUser (data) {
    return this._instance.request({
      method: 'put',
      url: 'me/',
      data
    })
  }

  patchUser (data) {
    return this._instance.request({
      method: 'patch',
      url: 'me/',
      data
    })
  }

  signUp (data: SignUpParameters) {
    return this._instance.request({
      method: 'post',
      url: 'signup/',
      data
    })
  }

  requestResetPassword (data: { email: string }) {
    return this._instance.request({
      method: 'post',
      url: 'password_reset/',
      data
    })
  }

  resetPassword (data: { password: string; token: string }) {
    return this._instance.request({
      method: 'post',
      url: 'password_reset/confirm/',
      data
    })
  }

  validateToken (data: { token: string }) {
    return this._instance.request({
      method: 'post',
      url: 'password_reset/validate_token/',
      data
    })
  }

  postDrawing (eventId, data: { draw: any[] }) {
    return this._instance.request({
      method: 'post',
      url: 'events/' + eventId + '/drawings/',
      data: { data }
    })
  }

  patchDrawing (eventId, drawingId, data: { draw: any[] }) {
    return this._instance.request({
      method: 'patch',
      url: 'events/' + eventId + '/drawings/' + drawingId + '/',
      data: { data }
    })
  }

  broadcastDrawing (eventId, drawingId) {
    return this._instance.request({
      method: 'post',
      url: 'events/' + eventId + '/drawings/' + drawingId + '/broadcast/'
    })
  }

  getDrawing (eventId, onlyBroadcasted: boolean) {
    return this._instance.request({
      method: 'get',
      url: 'events/' + eventId + '/drawings/' + (onlyBroadcasted ? 'readonly/' : '')
    })
  }
}

export const apiService = new ApiService()

export default apiService
